import React from 'react';
import { Box, Button, createTheme, Typography, useMediaQuery } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { appTheme, PHONE_QUERY } from '../theme';
import { humanize } from '../util/StringUtil';
import { MUIDataTableColumn, MUIDataTableColumnDef, MUIDataTableMeta } from 'mui-datatables';

declare module '@mui/material/styles' {
  interface Components {
    [key: string]: any;
  }
}

export const ROW_COLORS = {
  base: '#ffffff',
  striped: '#e6f4f9', // rgba(0, 143, 197, 0.1) on white
  hover: '#b2ddee', // rgba(0, 143, 197, 0.3) on white
  selected: '#66bcdc', // rgba(0, 143, 197, 0.6) on white
};

// Builds on the app-wide theme (e.g. sentence-case buttons) and adds the data table styling.
export const getMuiTheme = () =>
  createTheme(appTheme, {
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: '1.1em',
            whiteSpace: 'pre-wrap',
          },
        },
      },
      MUIDataTable: {
        styleOverrides: {
          paper: {
            boxShadow: 'none',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            justifyContent: 'center',
            backgroundColor: 'white',
            height: 32,
            minHeight: 32,
            '@media (min-width: 600px)': {
              minHeight: 32,
            },
          },
        },
      },
      MUIDataTableFilterList: {
        styleOverrides: {
          root: {
            paddingBottom: '8px',
          },
        },
      },
      MUIDataTableBodyRow: {
        styleOverrides: {
          // Solid colours (the old translucent tints over white) so the pinned checkbox cell,
          // which inherits the row background, hides the columns scrolling underneath it.
          root: {
            backgroundColor: ROW_COLORS.base,
            '&.Mui-selected': {
              backgroundColor: `${ROW_COLORS.selected} !important`,
            },
            '&:nth-of-type(even)': {
              backgroundColor: ROW_COLORS.striped,
            },
            '&:hover': {
              backgroundColor: `${ROW_COLORS.hover} !important`,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            justifyContent: 'center',
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          hintIconWithSortIcon: {
            color: 'darkgray',
            fontSize: '16px',
            marginLeft: 0,
          },
          hintIconAlone: {
            color: 'darkgray',
            fontSize: '16px',
            marginLeft: 0,
          },
          contentWrapper: {
            display: 'inline',
          },
          toolButton: {
            lineHeight: 1.35,
            margin: 0, // remove the weird alignment it gives otherwise to the header
          },
          root: {
            textAlign: 'center',
            backgroundColor: '#ededed',
            padding: 0,
            lineHeight: 1.35,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            letterSpacing: 0,
          },
          body: {
            padding: 1,
          },
          paddingCheckbox: {
            padding: '0px !important',
          },
        },
      },
      MUIDataTableBodyCell: {
        styleOverrides: {
          // Stacked layout (below md): columns set their own alignment for the desktop table,
          // which made labels and values jump between left, centre and right. Left-align them all.
          root: {
            '@media (max-width:899.95px)': {
              textAlign: 'left',
              // Some columns pad the right of numbers; in the stacked layout that just shifts the value left.
              paddingRight: '1px !important',
              // Cell content centred with flex (tags, icons, buttons) starts on the left too.
              '& div': {
                justifyContent: 'flex-start',
              },
            },
          },
        },
      },
      MUIDataTableSelectCell: {
        styleOverrides: {
          fixedLeft: {
            backgroundColor: 'inherit',
          },
          headerCell: {
            lineHeight: 1.35,
            backgroundColor: '#ededed !important',
          },
        },
      },
    },
  });

/**
 * Filter options for a true/false column, so the filter menu and the active-filter chip read
 * "On"/"Off" (or the given labels) instead of a blank chip for false.
 */
export const booleanFilter = (prefix: string, trueLabel = 'On', falseLabel = 'Off') => ({
  filterOptions: {
    names: [trueLabel, falseLabel],
    logic: (value: boolean, filters: string[]) => filters.length > 0 && !filters.includes(value ? trueLabel : falseLabel),
  },
  customFilterListOptions: { render: (label: string) => `${prefix}: ${label}` },
});

export const customFilterWithPrefix = (prefix: string) => {
  return {
    customFilterListOptions: {
      render: (value: any) => {
        if (value == null) return prefix + ': None';
        return prefix + ': ' + value;
      },
    },
  };
};

/** A column's values from the filterList mui-datatables passes to onFilterChange (indexed like columns). */
export function getColumnFilter(columns: MUIDataTableColumnDef[], filterList: string[][], columnName: string): string[] {
  return getColumnFilters(columns, filterList)[columnName] || [];
}

/** The filtered values of every column by column name, from a filterList indexed like columns. Columns with no filter are left out. */
export function getColumnFilters(columns: MUIDataTableColumnDef[], filterList: string[][]): Record<string, string[]> {
  const filters: Record<string, string[]> = {};
  columns.forEach((column, index) => {
    if (typeof column !== 'string' && filterList[index]?.length) {
      filters[column.name] = filterList[index];
    }
  });
  return filters;
}

/**
 * Whether a data row passes the filters (selected values by column name), the way mui-datatables filters its rows for
 * dropdown/multiselect filters (all the dashboard uses): the column's filterOptions.logic when it has one, otherwise the
 * value must be one of the selected values. Like the table, the value is what a customBodyRender returns, if it returns text.
 */
export function matchesFilters(columns: MUIDataTableColumnDef[], row: object, filters: Record<string, string[]>): boolean {
  const namedColumns = columns.filter(column => typeof column !== 'string') as MUIDataTableColumn[];
  const valueOf = (name: string) => name.split('.').reduce((value: any, key) => value?.[key], row);
  // The whole row (indexed like the columns) is only needed by custom logic and renders, so it's built when first needed
  let rowData: unknown[] | undefined;
  const getRowData = () => (rowData ??= namedColumns.map(column => valueOf(column.name)));
  return namedColumns.every(({ name, options = {} }) => {
    const selected = filters[name] || [];
    if (!selected.length) return true;
    let value = valueOf(name);
    if (options.customBodyRender && !options.customBodyRenderLite) {
      const rendered = options.customBodyRender(value, { rowData: getRowData() } as MUIDataTableMeta, () => {});
      value = typeof rendered === 'string' || !rendered ? rendered : (rendered as React.ReactElement).props?.value || value;
    }
    if (options.filterOptions?.logic) return !options.filterOptions.logic(value, selected, getRowData());
    return Array.isArray(value) ? selected.some(filter => value.includes(filter)) : selected.includes(value);
  });
}

/** The filters as the table's filter chips show them, e.g. ["Status: Pending", "Tag: bas"]. Columns that aren't there are left out. */
export function describeFilters(columns: MUIDataTableColumnDef[], filters: Record<string, string[]>): string[] {
  return columns.flatMap(column => {
    if (typeof column === 'string') return [];
    const render = column.options?.customFilterListOptions?.render;
    return (filters[column.name] || []).map(value => (render ? String(render(value)) : `${column.label || column.name}: ${value}`));
  });
}

/** Make the columns' filters controlled: each column shows the values in filters (by column name), or none. */
export function withFilterLists(columns: MUIDataTableColumnDef[], filters: Record<string, string[]>): MUIDataTableColumnDef[] {
  return columns.map(column => (typeof column === 'string' ? column : { ...column, options: { ...column.options, filterList: filters[column.name] || [] } }));
}

/**
 * Shown instead of mui-datatables' "Sorry, no matching records found" when search or filters hide every row.
 * Sticky so it stays in view when the table is scrolled sideways.
 */
export function NoMatches(props: { message: string; onClear?: () => void; clearLabel?: string }) {
  return (
    <Box
      sx={{ position: 'sticky', left: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, py: 3, px: 2, maxWidth: 640, textAlign: 'left' }}
    >
      <SearchOffIcon sx={{ color: 'text.disabled' }} />
      <Box sx={{ minWidth: 200 }}>
        <Typography sx={{ fontWeight: 600 }}>{props.message}</Typography>
        <Typography variant="body2" color="text.secondary">
          Try a different search, or remove a filter above.
        </Typography>
      </Box>
      {props.onClear && (
        <Button size="small" variant="outlined" onClick={props.onClear} sx={{ ml: 1, whiteSpace: 'nowrap' }}>
          {props.clearLabel || 'Clear filter'}
        </Button>
      )}
    </Box>
  );
}

/** How a raw column value reads in filter menus and chips: "MONTHLY" -> "Monthly", empty -> "None". */
export function displayFilterValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'None';
  if (typeof value === 'string' && /^[A-Z_]+$/.test(value)) return humanize(value);
  return String(value);
}

/**
 * Give every filterable column a readable filter: the menu shows displayFilterValue, and the active-filter
 * chip says which column it is ("PAYGW Period: Monthly"). Columns with their own chip or options keep them.
 */
export function withReadableFilters(columns: MUIDataTableColumnDef[]): MUIDataTableColumnDef[] {
  return columns.map(column => {
    if (typeof column === 'string' || column.options?.filter === false) return column;
    const options = column.options || {};
    return {
      ...column,
      options: {
        ...options,
        filterOptions: options.filterOptions || { renderValue: displayFilterValue },
        customFilterListOptions: options.customFilterListOptions || {
          render: (value: unknown) => `${column.label || column.name}: ${displayFilterValue(value)}`,
        },
      },
    };
  });
}

/** Fixed table height (scrolling inside the table) on larger screens; on phones the page scrolls instead of a small inner box. */
export function useTableBodyHeight(height: string): string {
  return useMediaQuery(PHONE_QUERY) ? 'auto' : height;
}
